import { type MutableRefObject, type ReactNode } from 'react';

import { Box } from '@mui/material';

import { useReportContext } from '@modules/_shared/components/report-editor/ReportEditorContext';
import ReportEditorStepper from '@modules/_shared/components/report-editor/ReportEditorStepper';
import ReportEditorStepperComponent from '@modules/_shared/components/report-editor/ReportEditorStepperComponent';
type LayoutMode = 'desktop' | 'mobile';

type BaseLayoutProps = {
  mode: LayoutMode;
  renderPreview: (props: {
    iFrameRef: MutableRefObject<HTMLIFrameElement | null>;
  }) => ReactNode;
  editorEl?: MutableRefObject<HTMLDivElement | null>;
  containerEl?: MutableRefObject<HTMLDivElement | null>;
};

const builderContent = (
  <ReportEditorStepper>
    <ReportEditorStepperComponent />
  </ReportEditorStepper>
);

const BaseLayout = ({
  mode,
  renderPreview,
  editorEl,
  containerEl,
}: BaseLayoutProps) => {
  const { iFrameRef } = useReportContext();
  const previewContent = renderPreview({ iFrameRef });

  if (mode === 'desktop') {
    return (
      <Box
        sx={{
          display: 'flex',
          overflow: 'hidden',
          height: '100%',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 1,
            flexShrink: 0,
            width: 'auto',
            borderRight: '1px solid',
            borderColor: 'divider',
          }}
        >
          {builderContent}
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            position: 'relative',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {previewContent}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Box
        ref={containerEl}
        sx={{
          display: 'flex',
          overflowX: 'auto',
          overflowY: 'hidden',
          touchAction: 'pan-y',
          flex: 1,
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
      >
        <Box
          ref={editorEl}
          css={{
            position: 'relative',
            overflowY: 'auto',
            minWidth: '100vw',
          }}
        >
          {builderContent}
        </Box>
        {previewContent}
      </Box>
    </Box>
  );
};

export default BaseLayout;
